import React, { useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { SelectInput, TextInput } from '@wix/cli-ui-kit';
import type { VariantOf } from 'variant';
import { fields, isType, match, variant } from 'variant';
import { useTranslation } from '@wix/cli-i18n';
import type { ActionSchema } from '@wix/cli-app-definitions';
import { useProjectModel } from '../../../../model.js';
import { DevCenterComponent } from '../../../../dev-center/components/dev-center-component.js';
import {
  composeValidations,
  useValidation,
  uuidValidation,
} from '../../../../dev-center/components/validations/index.js';
import { useWixCliAppBi } from '../../../../bi/index.js';

const NavigateToPageOption = variant({
  Page: fields<{ pageId: string }>(),
  Other: {},
});

type NavigateToPageOption = VariantOf<typeof NavigateToPageOption>;

export const NavigateToPage: FC<{
  onActionSelected: (action: ActionSchema) => void;
}> = ({ onActionSelected }) => {
  const bi = useWixCliAppBi();
  const { model } = useProjectModel();
  const { t } = useTranslation();
  const [pageId, setPageId] = useState<string>();
  const [shouldUseCustomPageId, setShouldUseCustomPageId] =
    useState<boolean>(false);

  const dashboardPages = useMemo(
    () => model.components.filter(isType(DevCenterComponent.DashboardPage)),
    [model.components]
  );

  const validatePageId = useValidation(
    composeValidations([
      [
        uuidValidation,
        t(
          'generate_command.dashboard_menu_plugin.what_to_do.navigate_to_page.choose_page.other.page_id_invalid'
        ),
      ],
    ])
  );

  useEffect(() => {
    if (pageId) {
      onActionSelected({
        navigateToPage: {
          pageId,
        },
      });
    }
  }, [pageId, onActionSelected]);

  return (
    <>
      <SelectInput<NavigateToPageOption>
        label={t(
          'generate_command.dashboard_menu_plugin.what_to_do.navigate_to_page.choose_page'
        )}
        info={t(
          'generate_command.dashboard_menu_plugin.what_to_do.navigate_to_page.choose_page.info'
        )}
        options={[
          ...dashboardPages.map(({ id, title }) => ({
            key: id,
            title,
            value: NavigateToPageOption.Page({ pageId: id }),
          })),
          {
            key: 'other',
            title: t(
              'generate_command.dashboard_menu_plugin.what_to_do.navigate_to_page.choose_page.other'
            ),
            value: NavigateToPageOption.Other(),
          },
        ]}
        onSubmit={(option) => {
          bi.cliFlowStepAnswered({
            question: t(
              'generate_command.dashboard_menu_plugin.what_to_do.navigate_to_page.choose_page'
            ),
            questionKey:
              'generate_command.dashboard_menu_plugin.what_to_do.navigate_to_page.choose_page',
            answer: option.title,
          });
          return match(option.value, {
            Page: ({ pageId }) => setPageId(pageId),
            Other: () => setShouldUseCustomPageId(true),
          });
        }}
      />
      {shouldUseCustomPageId && (
        <TextInput
          label={t(
            'generate_command.dashboard_menu_plugin.what_to_do.navigate_to_page.choose_page.other.choose_page_id'
          )}
          onSubmit={(pageId) => {
            bi.cliFlowStepAnswered({
              question: t(
                'generate_command.dashboard_menu_plugin.what_to_do.navigate_to_page.choose_page.other.choose_page_id'
              ),
              questionKey:
                'generate_command.dashboard_menu_plugin.what_to_do.navigate_to_page.choose_page.other.choose_page_id',
              answer: pageId,
            });
            setPageId(pageId);
          }}
          validate={validatePageId}
        />
      )}
    </>
  );
};
